import { useLocation, useNavigate } from 'react-router-dom';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  useSidebar,
} from '@/components/ui/sidebar';
import { 
  LayoutDashboard, 
  Package, 
  Building2, 
  Network, 
  Cable, 
  Library, 
  TrendingUp, 
  GitPullRequest, 
  FileText, 
  Database,
  Settings,
  Shield,
  History,
  Plug,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Radar,
  Zap,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import logoImage from '@/assets/logo.png';
import { useAuth } from '@/hooks/useAuth';

const MAIN_NAV = [
  { path: '/app', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/inventory', label: 'Inventory', icon: Package },
  { path: '/inventory/data-centers', label: 'Infrastructure', icon: Building2 },
  { path: '/ipam', label: 'IPAM', icon: Network },
  { path: '/cables', label: 'Cables', icon: Cable },
];

const TOOLS_NAV = [
  { path: '/inventory/device-library', label: 'Device Library', icon: Library },
  { path: '/inventory/capacity', label: 'Capacity Forecast', icon: TrendingUp },
  { path: '/inventory/3d', label: '3D Visualization', icon: Building2 },
  { path: '/monitoring', label: 'Power Monitoring', icon: Zap },
  { path: '/discovery', label: 'Network Discovery', icon: Radar },
  { path: '/workorders', label: 'Work Orders', icon: ClipboardList },
  { path: '/changes', label: 'Change Management', icon: GitPullRequest },
  { path: '/reports', label: 'Reports', icon: FileText },
  { path: '/dashboard', label: 'Data Validation', icon: Shield },
];

const ADMIN_NAV = [
  { path: '/integrations', label: 'Integrations', icon: Plug },
  { path: '/api', label: 'API Management', icon: Plug },
  { path: '/app/audit-logs', label: 'Audit Logs', icon: History },
  { path: '/settings', label: 'Settings', icon: Settings },
];

export function AppSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, toggleSidebar } = useSidebar();
  const { isAdmin, companyName } = useAuth();
  const collapsed = state === 'collapsed';

  const isActive = (path: string) => {
    if (path === '/app') return location.pathname === '/app';
    if (path === '/inventory') return location.pathname === '/inventory' || location.pathname.startsWith('/inventory/assets') || location.pathname.startsWith('/inventory/types');
    if (path === '/inventory/device-library') return location.pathname === '/inventory/device-library';
    if (path === '/inventory/capacity') return location.pathname === '/inventory/capacity';
    if (path === '/inventory/3d') return location.pathname === '/inventory/3d';
    if (path === '/inventory/data-centers') return location.pathname.startsWith('/inventory/data-centers') || location.pathname.startsWith('/inventory/data-halls') || location.pathname.startsWith('/inventory/rows') || location.pathname.startsWith('/inventory/racks');
    if (path === '/ipam') return location.pathname.startsWith('/ipam');
    if (path === '/cables') return location.pathname.startsWith('/cables');
    if (path === '/workorders') return location.pathname.startsWith('/workorders');
    if (path === '/changes') return location.pathname.startsWith('/changes');
    if (path === '/reports') return location.pathname.startsWith('/reports');
    if (path === '/discovery') return location.pathname.startsWith('/discovery');
    if (path === '/api') return location.pathname.startsWith('/api');
    if (path === '/app/audit-logs') return location.pathname === '/app/audit-logs';
    if (path === '/settings') return location.pathname === '/settings';
    if (path === '/dashboard') return location.pathname === '/dashboard' || location.pathname.startsWith('/site/') || location.pathname.startsWith('/data-hall/') || location.pathname === '/kpi' || location.pathname === '/operations' || location.pathname === '/validation' || location.pathname === '/validation-rules';
    return location.pathname.startsWith(path);
  };

  const NavItem = ({ item }: { item: typeof MAIN_NAV[0] }) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          onClick={() => navigate(item.path)}
          className={cn(
            'w-full justify-start gap-3 transition-colors',
            active 
              ? 'bg-primary/10 text-primary font-medium' 
              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
          )}
          tooltip={collapsed ? item.label : undefined}
        >
          <Icon className="h-4 w-4 shrink-0" />
          {!collapsed && <span>{item.label}</span>}
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar collapsible="icon" className="border-r border-border">
      <SidebarHeader className="p-4">
        <div 
          className={cn(
            "flex items-center gap-3 cursor-pointer",
            collapsed && "justify-center"
          )}
          onClick={() => navigate('/app')}
        >
          <img 
            src={logoImage} 
            alt="IMRACK" 
            className="h-8 w-8 rounded-lg object-contain shrink-0"
          />
          {!collapsed && (
            <div className="overflow-hidden">
              <h1 className="text-base font-semibold text-foreground truncate">
                {companyName || 'IMRACK'}
              </h1>
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent className="px-2">
        {/* Main Navigation */}
        <SidebarGroup>
          {!collapsed && <SidebarGroupLabel className="text-xs text-muted-foreground px-2">Main</SidebarGroupLabel>}
          <SidebarGroupContent>
            <SidebarMenu>
              {MAIN_NAV.map((item) => (
                <NavItem key={item.path} item={item} />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Tools */}
        <SidebarGroup className="mt-4">
          {!collapsed && <SidebarGroupLabel className="text-xs text-muted-foreground px-2">Tools</SidebarGroupLabel>}
          <SidebarGroupContent>
            <SidebarMenu>
              {TOOLS_NAV.map((item) => (
                <NavItem key={item.path} item={item} />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Admin */}
        {isAdmin && (
          <SidebarGroup className="mt-4">
            {!collapsed && <SidebarGroupLabel className="text-xs text-muted-foreground px-2">Admin</SidebarGroupLabel>}
            <SidebarGroupContent>
              <SidebarMenu>
                {ADMIN_NAV.map((item) => (
                  <NavItem key={item.path} item={item} />
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="p-2 border-t border-border">
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleSidebar}
          className={cn(
            "w-full justify-center gap-2",
            !collapsed && "justify-start"
          )}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <>
              <ChevronLeft className="h-4 w-4" />
              <span>Collapse</span>
            </>
          )}
        </Button>
      </SidebarFooter>
    </Sidebar>
  );
}
